import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  signal,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { APP_MODE } from '@ftr/routing-paths'
import { debounceTime, filter, fromEvent, map, startWith } from 'rxjs'
import { ScreenSize, ScreenSizeService, SimpleWindowRefService } from '../../services'
import { BreadcrumbService } from './breadcrumb.service'

/**
 *
 * Renders breadcrumbs for the activated route.
 *
 * Breadcrumbs are derived from the route hierarchy. BreadcrumbService traverses the route hierarchy top-down and looks
 * for a breadcrumb string in the route data, using that and the route's URL to build a Breadcrumb item.
 *
 * For dynamic breadcrumb, a data resolver can be used by implementing the helper type BreadcrumbResolver and returning
 * the desired breadcrumb label (e.g.: fetching a court system's name by its id).
 *
 * @example
 *
 * // Route setup
 *
 * [{
 *   path: '',
 *   data: {
 *     breadcrumb: 'Breadcrumb for parent route'
 *   },
 *   children: [
 *     {
 *       path: 'child',
 *       resolve: {
 *         data: CustomBreadcrumbResolver
 *       }
 *     }
 *   ]
 * }]
 *
 * // CustomBreadcrumbResolver
 *
 * export class CustomBreadcrumbResolver implements BreadcrumbResolver {
 *   resolve (
 *     route: ActivatedRouteSnapshot,
 *     state: RouterStateSnapshot
 *   ): (string | undefined) | Promise<string | undefined> | Observable<string | undefined> {
 *     return 'Dynamic breadcrumb label'
 *   }
 * }
 *
 * // Resulting breadcrumb for the 'child' route
 *
 * [
 *   {
 *     label: 'Breadcrumb for parent route',
 *     url: '/'
 *   },
 *   {
 *     label: 'Dynamic breadcrumb label',
 *     url: '/child
 *   }
 * ]
 *
 */
@Component({
  selector: 'ftr-breadcrumbs',
  templateUrl: 'breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.css'],
  standalone: false,
})
export class BreadcrumbsComponent implements AfterViewInit {
  private readonly breadcrumbsService = inject(BreadcrumbService)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly screenSizeService = inject(ScreenSizeService)
  private readonly windowService = inject(SimpleWindowRefService)
  private readonly appMode = inject(APP_MODE)

  protected breadcrumbs = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.breadcrumbsService.getBreadcrumbs(this.activatedRoute.root, this.appMode())),
      startWith(this.breadcrumbsService.getBreadcrumbs(this.activatedRoute.root, this.appMode())),
      map(breadcrumbs => (breadcrumbs.length === 1 && breadcrumbs[0].link.url === '/' ? [] : breadcrumbs)),
    ),
    { initialValue: [] },
  )

  protected shouldCollapse = computed(() => {
    // If there's <= 2 crumbs, the breadcrumb just includes Home and the leaf node
    // So there's nothing to collapse
    const enoughCrumbsToCollapse = this.breadcrumbs().length > 2
    const mediumDownViewport = this.screenSizeService.sizeSignal() <= ScreenSize.Medium
    return enoughCrumbsToCollapse && (mediumDownViewport || this.exceedsViewportWidth())
  })

  protected clampLeaf = signal<boolean>(false)
  protected clampLink = signal<boolean>(false)
  protected flexExpandLinkNode = signal<boolean>(false)
  private exceedsViewportWidth = signal<boolean>(false)

  private injector = inject(Injector)

  @ViewChild('outerCrumbs') outerCrumbs: ElementRef<HTMLElement>
  @ViewChild('innerCrumbs') innerCrumbs: ElementRef<HTMLElement>
  @ViewChild('measurementCrumbs') measurementCrumbs: ElementRef<HTMLElement>

  constructor() {
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        afterNextRender(() => this.calculateBreadcrumbContentCollapse(), { injector: this.injector })
      })

    fromEvent(this.windowService.nativeWindow(), 'resize')
      .pipe(takeUntilDestroyed(), debounceTime(100))
      .subscribe(() => {
        this.calculateBreadcrumbContentCollapse()
      })
  }

  ngAfterViewInit(): void {
    this.calculateBreadcrumbContentCollapse()
  }

  private calculateBreadcrumbContentCollapse(): void {
    const elementHeight =
      this.measurementCrumbs.nativeElement.querySelector('.breadcrumbs')?.getBoundingClientRect().height || 0
    const parentHeight = this.outerCrumbs.nativeElement.offsetHeight
    this.exceedsViewportWidth.set(elementHeight > parentHeight)

    const leafCrumbHeight =
      this.innerCrumbs.nativeElement.querySelector('.breadcrumbs__item--current')?.getBoundingClientRect().height || 0
    const leafCrumbScrollHeight =
      this.innerCrumbs.nativeElement.querySelector('.breadcrumbs__item--current')?.scrollHeight || 0
    this.clampLeaf.set(leafCrumbScrollHeight > leafCrumbHeight)

    const linkCrumb = this.innerCrumbs.nativeElement.querySelector('.breadcrumbs__link') as HTMLElement | null
    const linkCrumbHeight = linkCrumb?.getBoundingClientRect().height ?? 0
    const linkCrumbScrollHeight = linkCrumb?.scrollHeight ?? 0

    this.clampLink.set(linkCrumbScrollHeight > linkCrumbHeight)

    this.flexExpandLinkNode.set(this.calculateLeafLinkShouldExpand())
  }

  private calculateLeafLinkShouldExpand(): boolean {
    if (!this.innerCrumbs) {
      return false
    }
    const linkCrumbWidth =
      Array.from(this.innerCrumbs.nativeElement.querySelectorAll('.breadcrumbs__item')).pop()?.getBoundingClientRect()
        .width ?? 0
    const measurementCrumbLinkWidth =
      Array.from(this.measurementCrumbs.nativeElement.querySelectorAll('.breadcrumbs__item'))
        .pop()
        ?.getBoundingClientRect().width ?? 0

    return measurementCrumbLinkWidth > linkCrumbWidth
  }
}
